import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import ErrorBanner from '../shared/ErrorBanner'
import InlineLoading from '../shared/InlineLoading'
import Markdown from '../shared/Markdown'
import {
    lookedUpLabel,
    retryableFailures,
    starterQuestions,
} from '../../helpers/assistant'
import { BOT, BRAND, RADIUS } from '../../theme/tokens'

const bubbleSx = {
    maxWidth: '88%',
    px: 1.5,
    py: 1,
    typography: 'body2',
    lineHeight: 1.55,
    overflowWrap: 'anywhere',
}

/** The person's message: their words as typed, on the right. */
const UserBubble = ({ text }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box
            data-testid="bot-user-message"
            sx={{
                ...bubbleSx,
                backgroundColor: BOT.userBubble,
                color: BOT.userText,
                borderRadius: `${RADIUS.md}px ${RADIUS.md}px ${RADIUS.xs}px ${RADIUS.md}px`,
                whiteSpace: 'pre-wrap',
            }}
        >
            {text}
        </Box>
    </Box>
)
UserBubble.propTypes = { text: PropTypes.string }

/**
 * One of BhanzuBot's answers, on the left: its markdown (untrusted, shown
 * safely), what it looked up, or, for an answer that failed, the reason and
 * a Retry when this is the question's latest try.
 */
const BotBubble = ({ message, canRetry, onRetry, retryDisabled }) => {
    const lookedUp = lookedUpLabel(message.looked_up)
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box
                data-testid="bot-answer"
                sx={{
                    ...bubbleSx,
                    backgroundColor: BOT.botBubble,
                    color: BOT.botText,
                    borderRadius: `${RADIUS.md}px ${RADIUS.md}px ${RADIUS.md}px ${RADIUS.xs}px`,
                    '& > div > :last-child': { mb: 0 },
                }}
            >
                {message.failed ? (
                    <>
                        <Typography
                            role="alert"
                            sx={{ typography: 'body2', color: BOT.errorText }}
                        >
                            BhanzuBot could not answer
                            {message.failure_reason
                                ? `: ${message.failure_reason}`
                                : '.'}
                        </Typography>
                        {canRetry && (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => onRetry(message.answers)}
                                disabled={retryDisabled}
                                sx={{ mt: 0.5, ml: -1 }}
                            >
                                Retry
                            </Button>
                        )}
                    </>
                ) : (
                    <Markdown dense>{message.text}</Markdown>
                )}
                {lookedUp && (
                    <Typography
                        sx={{
                            typography: 'caption',
                            color: BOT.meta,
                            mt: 0.75,
                        }}
                    >
                        Looked up: {lookedUp}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
BotBubble.propTypes = {
    message: PropTypes.object.isRequired,
    canRetry: PropTypes.bool,
    onRetry: PropTypes.func.isRequired,
    retryDisabled: PropTypes.bool,
}

/**
 * The conversation: a greeting and three questions for this page when it
 * is empty, the messages oldest first, the question on its way with
 * "BhanzuBot is looking this up…", and the newest kept in view.
 */
const ChatView = ({
    pathname,
    messages,
    pendingQuestion,
    pending,
    loadingConversation,
    error,
    disabled,
    onAsk,
    onRetry,
}) => {
    const endRef = useRef(null)
    const retryable = useMemo(() => retryableFailures(messages), [messages])

    useEffect(() => {
        const end = endRef.current
        if (end && typeof end.scrollIntoView === 'function')
            end.scrollIntoView({ block: 'end' })
    }, [messages.length, pendingQuestion, pending])

    if (loadingConversation)
        return <InlineLoading label="Loading the conversation…" py={6} />

    const empty = messages.length === 0 && !pendingQuestion
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                p: 1.5,
            }}
        >
            {empty && (
                <Box>
                    <Typography
                        sx={{
                            typography: 'body2',
                            color: BOT.botText,
                            mb: 1.5,
                        }}
                    >
                        Hi, I am BhanzuBot. Ask me how something works in Comms,
                        or about your templates, pipelines and sends.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {starterQuestions(pathname).map((question) => (
                            <Button
                                key={question}
                                variant="outlined"
                                size="small"
                                onClick={() => onAsk(question)}
                                disabled={pending || Boolean(disabled)}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                }}
                            >
                                {question}
                            </Button>
                        ))}
                    </Box>
                </Box>
            )}
            {messages.map((message) =>
                message.sender === 'user' ? (
                    <UserBubble key={message.id} text={message.text} />
                ) : (
                    <BotBubble
                        key={message.id}
                        message={message}
                        canRetry={retryable.has(message.id)}
                        onRetry={onRetry}
                        retryDisabled={pending || Boolean(disabled)}
                    />
                )
            )}
            {pendingQuestion && <UserBubble text={pendingQuestion} />}
            {pending && (
                <Box
                    role="status"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <CircularProgress
                        size={14}
                        aria-hidden
                        sx={{ color: BRAND.orange }}
                    />
                    <Typography sx={{ typography: 'caption', color: BOT.meta }}>
                        BhanzuBot is looking this up…
                    </Typography>
                </Box>
            )}
            {error && <ErrorBanner message={error} />}
            {disabled && (
                <Typography
                    role="status"
                    sx={{ typography: 'body2', color: BOT.meta }}
                >
                    {disabled}
                </Typography>
            )}
            <div ref={endRef} />
        </Box>
    )
}

ChatView.propTypes = {
    pathname: PropTypes.string.isRequired,
    messages: PropTypes.array.isRequired,
    pendingQuestion: PropTypes.string,
    pending: PropTypes.bool,
    loadingConversation: PropTypes.bool,
    error: PropTypes.string,
    disabled: PropTypes.string,
    onAsk: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
}

export default ChatView
